import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {InventoryFeature, RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import * as RegistryUtil from 'registry/RegistryUtil';
import getRegistrySchemasTitle from 'settings/registry/getRegistrySchemasTitle';
import {RegistrySettingsQuery} from 'sidebar/__generated/RegistrySettingsQuery';
import {RegistrySettingsWithValidationFailuresQuery} from 'sidebar/__generated/RegistrySettingsWithValidationFailuresQuery';
import WarningBadge from 'ui-library/WarningBadge';
import {Optional} from 'util/Types';

import EmptySidebarIcon from './EmptySidebarIcon';
import * as SidebarConstants from './SidebarConstants';
import SidebarItem from './SidebarItem';

type QueryRegistriesWithValidationFailures =
  RegistrySettingsWithValidationFailuresQuery['registries'][number];
type QueryRegistries = RegistrySettingsQuery['registries'][number];

export const REGISTRY_SETTINGS_WITH_VALIDATION_FAILURES_QUERY_POLL_INTERVAL_MS = 120_000;

export const REGISTRY_SETTINGS_QUERY = gql`
  query RegistrySettingsQuery @skipAudit @engComponent(name: REGISTRY) {
    registries {
      ... on RegistryNode {
        name
        id
        ownerJson
      }
    }
  }
`;

export const REGISTRY_SETTINGS_WITH_VALIDATION_FAILURES_QUERY = gql`
  query RegistrySettingsWithValidationFailuresQuery @skipAudit @engComponent(name: REGISTRY) {
    registries {
      ... on RegistryNode {
        name
        id
        ownerJson
        numBioentityValidationFailures
        numLocationValidationFailures
      }
    }
  }
`;

interface Props {
  section: SidebarConstants.Section;
  activeRegistryId: string | null;
}

interface RegistryValidationFailures {
  numBioentityFailures: number;
  numLocationFailures: number;
}

interface QueryingProps extends Props {
  registries: Array<QueryRegistries> | Array<QueryRegistriesWithValidationFailures>;
  numErrorsByRegistryId: Map<string, RegistryValidationFailures> | null;
}

export class RegistrySettings extends React.Component<QueryingProps> {
  private renderRegistrySection(
    registry: QueryRegistries,
    registryErrors: Optional<RegistryValidationFailures>
  ): JSX.Element {
    return (
      <div
        className="u-spacing-bottom"
        key={registry.id}
        {...getTestProps({component: 'RegistrySettings', element: 'root'})}
      >
        {}
        <div
          className="browser-nav-header"
          {...getTestProps({component: 'RegistrySettings', element: 'header'})}
        >{
          // TODO (CUST-3890) Just use registry.name
          `${registry.name === 'Registry' ? `${registry.ownerJson.name} ` : ''}${registry.name} Settings`
        }</div>
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_ADMIN}) && (
          <SidebarItem
            active={
              this.props.section === SidebarConstants.Section.REGISTRY_SETTINGS_GENERAL &&
              registry.id === this.props.activeRegistryId
            }
            icon={<EmptySidebarIcon />}
            name="General"
            link={RegistryUtil.getRegistryGeneralSettingsUrl(registry.ownerJson.url_identifier, registry.id)}
            dataTestAttrsKey="general"
          />
        )}
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_ADMIN}) && (
          <SidebarItem
            active={
              SidebarConstants.REGISTRY_SELECTORS_SECTIONS.includes(this.props.section) &&
              registry.id === this.props.activeRegistryId
            }
            icon={<EmptySidebarIcon />}
            // Note that these are called 'selectors' or 'schema-field-selectors' on the backend
            name="Dropdowns"
            link={RegistryUtil.getRegistrySelectorsSettingsUrl(
              registry.ownerJson.url_identifier,
              registry.id
            )}
            dataTestAttrsKey="dropdowns"
          />
        )}
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_ADMIN}) && (
          <SidebarItem
            active={
              SidebarConstants.REGISTRY_SCHEMAS_SECTIONS.includes(this.props.section) &&
              registry.id === this.props.activeRegistryId
            }
            icon={<EmptySidebarIcon />}
            name={getRegistrySchemasTitle()}
            badge={
              registryErrors != null &&
              registryErrors.numBioentityFailures > 0 && (
                <WarningBadge
                  content={registryErrors.numBioentityFailures}
                  tooltipTitle="Entities failed validation checks"
                  className={css(styles.sidebarItemWarningBadge)}
                  dataTestAttrs={{component: 'RegistrySettings', element: 'entitySchemaWarningBadge'}}
                />
              )
            }
            link={RegistryUtil.getRegistrySchemasSettingsUrl(registry.ownerJson.url_identifier, registry.id)}
            dataTestAttrsKey="entitySchemas"
          />
        )}
        {isFeatureEnabled({feature: InventoryFeature.INVENTORY_ADMIN}) && (
          <SidebarItem
            active={
              this.props.section === SidebarConstants.Section.REGISTRY_SETTINGS_CONTAINER_SCHEMAS &&
              registry.id === this.props.activeRegistryId
            }
            icon={<EmptySidebarIcon />}
            name="Container schemas"
            link={RegistryUtil.getRegistryContainerSchemasUrl(registry.ownerJson.url_identifier, registry.id)}
            dataTestAttrsKey="containerSchemas"
          />
        )}
        {isFeatureEnabled({feature: InventoryFeature.INVENTORY_ADMIN}) && (
          <SidebarItem
            active={
              this.props.section === SidebarConstants.Section.REGISTRY_SETTINGS_BOX_SCHEMAS &&
              registry.id === this.props.activeRegistryId
            }
            icon={<EmptySidebarIcon />}
            name="Box schemas"
            link={RegistryUtil.getRegistryBoxSchemasUrl(registry.ownerJson.url_identifier, registry.id)}
            dataTestAttrsKey="boxSchemas"
          />
        )}
        {isFeatureEnabled({feature: InventoryFeature.INVENTORY_ADMIN}) && (
          <SidebarItem
            active={
              this.props.section === SidebarConstants.Section.REGISTRY_SETTINGS_PLATE_SCHEMAS &&
              registry.id === this.props.activeRegistryId
            }
            icon={<EmptySidebarIcon />}
            name="Plate schemas"
            link={RegistryUtil.getRegistryPlateSchemasUrl(registry.ownerJson.url_identifier, registry.id)}
            dataTestAttrsKey="plateSchemas"
          />
        )}
        {isFeatureEnabled({feature: InventoryFeature.INVENTORY_ADMIN}) && (
          <SidebarItem
            active={
              this.props.section === SidebarConstants.Section.REGISTRY_SETTINGS_LOCATION_SCHEMAS &&
              registry.id === this.props.activeRegistryId
            }
            icon={<EmptySidebarIcon />}
            name="Location schemas"
            badge={
              registryErrors != null && registryErrors.numLocationFailures > 0 ? (
                <WarningBadge
                  content={registryErrors.numLocationFailures}
                  tooltipTitle="Locations failed validation checks"
                  className={css(styles.sidebarItemWarningBadge)}
                  dataTestAttrs={{component: 'RegistrySettings', element: 'locationSchemaWarningBadge'}}
                />
              ) : null
            }
            link={RegistryUtil.getRegistryLocationSchemasUrl(registry.ownerJson.url_identifier, registry.id)}
            dataTestAttrsKey="locationSchemas"
          />
        )}
        {isFeatureEnabled({feature: InventoryFeature.INVENTORY_ADMIN}) && (
          <SidebarItem
            active={
              this.props.section === SidebarConstants.Section.REGISTRY_SETTINGS_LABEL_PRINTING &&
              registry.id === this.props.activeRegistryId
            }
            icon={<EmptySidebarIcon />}
            name="Label printing"
            link={RegistryUtil.getRegistryLabelTemplatesUrl(registry.ownerJson.url_identifier, registry.id)}
            dataTestAttrsKey="labelPrinting"
          />
        )}
      </div>
    );
  }

  override render(): JSX.Element {
    return (
      <div {...getTestProps({component: 'RegistrySettings', element: 'sidebar'})}>
        {this.props.registries.map((r) =>
          this.renderRegistrySection(
            r,
            this.props.numErrorsByRegistryId ? this.props.numErrorsByRegistryId.get(r.id) : null
          )
        )}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  sidebarItemWarningBadge: {
    margin: '-3px 0',
  },
});

export default function QueryingRegistrySettings(props: Props): JSX.Element {
  const registriesQuery = useBenchlingQuery<RegistrySettingsQuery>(REGISTRY_SETTINGS_QUERY);
  const loadedPage = registriesQuery.type === QueryResultType.SUCCESS;
  const registriesWithoutValidationFailures = loadedPage ? registriesQuery.data.registries : [];
  const registriesWithValidationFailuresQuery =
    useBenchlingQuery<RegistrySettingsWithValidationFailuresQuery>(
      REGISTRY_SETTINGS_WITH_VALIDATION_FAILURES_QUERY,
      {
        fetchPolicy: 'cache-and-network',
        renderCachedDataWhenRefetching: true,
        pollInterval: REGISTRY_SETTINGS_WITH_VALIDATION_FAILURES_QUERY_POLL_INTERVAL_MS,
      }
    );
  const loadedValidationFailures = registriesWithValidationFailuresQuery.type === QueryResultType.SUCCESS;
  const registries = loadedValidationFailures
    ? registriesWithValidationFailuresQuery.data.registries
    : registriesWithoutValidationFailures;
  const numErrorsByRegistryId = loadedValidationFailures
    ? new Map(
        registriesWithValidationFailuresQuery.data.registries.map((registry) => [
          registry.id,
          {
            numBioentityFailures: registry.numBioentityValidationFailures,
            numLocationFailures: registry.numLocationValidationFailures,
          },
        ])
      )
    : null;
  return (
    <RegistrySettings {...props} registries={registries} numErrorsByRegistryId={numErrorsByRegistryId} />
  );
}
